import { IRequest } from 'legacy/lut/classes/models/onboarding/IRequest'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { ApolloSearch } from 'legacy/lut/components/common/apolloSearch/ApolloSearch'
import { ConfirmDialog } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/ConfirmDialog'
import { useChangePartnerId } from 'legacy/lut/components/widgets/public/onboarding/requestsInfo/requestTypes/modal/partnerIdModalDialog/hook/useChangePartnerId'
import { sortPartnerDifference } from 'legacy/lut/components/widgets/public/partnerDuplicates'
import React, { useCallback, useEffect, useState } from 'react'

import { IPartner } from 'clickNgo/entities/partner/models'
import { LoaderOverlay } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import logger from 'core/utils/Logger'
import { PartnerNotFoundError } from 'onboarding/classes/errors/PartnerNotFoundError'
import { PartnerHelper } from 'onboarding/classes/helpers/PartnerHelper'
import { showError } from 'onboarding/classes/services/notification'
import { requestService } from 'onboarding/services/requestService'
import { showErrorOnCatch } from 'onboarding/utils/errorResponse/showErrorOnCatch'
import { tryCatchAsync } from 'onboarding/utils/tryCatch'

import { useApolloData } from '../../hooks/useApolloData'

import styles from './Apollo.module.scss'

interface IApolloProps {
  request: IRequest
  partner: IPartner
  changeWip: (flag: boolean) => void
  isFormChanged: boolean
  apolloPartner: IPartner | null
  setApolloPartner: (partner: IPartner) => void
  setIsReactivateDialogVisible: (value: boolean) => void
}

export const Apollo = ({
  request,
  partner,
  changeWip,
  isFormChanged,
  apolloPartner,
  setApolloPartner,
  setIsReactivateDialogVisible,
}: IApolloProps) => {
  const [isConfirmDialogVisible, setIsConfirmDialogVisible] = useState(false)
  const [isRejectDialogVisible, setIsRejectDialogVisible] = useState(false)
  const [isTargetPartnerValidating, setIsTargetPartnerValidating] = useState(false)

  const onReplacePartner = useCallback(
    ({ partner }: { partner: IPartner }, callback?: () => void) => {
      setApolloPartner(partner)
      setIsConfirmDialogVisible(true)

      callback?.()
    },
    [setApolloPartner]
  )

  const onActivatePartner = useCallback(
    ({ partner }: { partner: IPartner }, callback?: () => void) => {
      setApolloPartner(partner)
      setIsReactivateDialogVisible(true)
      callback && callback()
    },
    [apolloPartner?.partnerId]
  )

  const {
    resetApolloResult,
    onPartnerSearchInApollo,
    partnerDuplicatesHeaderActions,
    partnerDuplicatesContentActions,
    partnerDuplicatesContentMessages,
    partnerDuplicatesHeaderMessages,
    apolloResult,
    update,
    isApolloResultVisible,
  } = useApolloData({
    changeLoader: changeWip,
    partner,
    request,
    onReplacePartner,
    onActivatePartner,
  })

  useEffect(() => {
    if (apolloPartner?.partnerId && apolloPartner.status) {
      update(apolloPartner?.partnerId, { status: apolloPartner.status })
    }
  }, [apolloPartner?.status])

  const openRejectDialog = useCallback(() => setIsRejectDialogVisible(true), [])

  const closeRejectAndConfirmDialog = useCallback(() => {
    setIsRejectDialogVisible(false)
    setIsConfirmDialogVisible(false)
  }, [])

  const closeAllModals = useCallback(() => {
    setIsRejectDialogVisible(false)
    setIsConfirmDialogVisible(false)
    resetApolloResult()
  }, [resetApolloResult])

  const {
    getModalContent,
    changePartnerId,
    showPreloader: showChangePartnerPreloader,
  } = useChangePartnerId({
    closeModal: closeRejectAndConfirmDialog,
    closeAllModals,
    request: request,
    openModal: openRejectDialog,
    isVisibleModal: isRejectDialogVisible,
  })

  const onChangePartnerIdHandler = useCallback(async () => {
    if (apolloPartner?.partnerId) {
      setIsTargetPartnerValidating(true)
      const { error } = await tryCatchAsync(
        requestService.canChangePartnerTarget(request.EntryId, {
          partnerId: apolloPartner.partnerId,
        })
      )
      setIsTargetPartnerValidating(false)
      if (error) {
        logger.error(error)
        if (error instanceof PartnerNotFoundError) {
          const partnerSearchId = error.getSearchId()
          const messageSearchId =
            'partnerId' in partnerSearchId
              ? `PartnerId='${partnerSearchId.partnerId}'`
              : `LocationId='${partnerSearchId.locationId}'`
          const message = `The partner ${messageSearchId} is not synchronized in Click & Go yet. Please wait for the next MDE feed update and retry after.`
          showError(message)

          return
        }

        showErrorOnCatch(error)

        return
      }

      changePartnerId(apolloPartner.partnerId)
    }
  }, [apolloPartner?.partnerId, changePartnerId, request.EntryId])

  return (
    <div className={styles.root}>
      {PartnerHelper.isDummyPartnerRequest(partner.partnerId) &&
      (request.Type === RequestTypes.Onboarding || request.Type === RequestTypes.Contract) ? (
        <ApolloSearch
          apolloResult={apolloResult}
          resetApolloResult={resetApolloResult}
          isDisabled={isFormChanged}
          isVisible={isApolloResultVisible}
          onPartnerSearchInApollo={onPartnerSearchInApollo}
          partnerDuplicatesHeaderActions={partnerDuplicatesHeaderActions}
          partnerDuplicatesHeaderMessages={partnerDuplicatesHeaderMessages}
          partnerDuplicatesContentMessages={partnerDuplicatesContentMessages}
          partnerDuplicatesContentActions={partnerDuplicatesContentActions}
          sortDifference={sortPartnerDifference}
        />
      ) : null}
      <ConfirmDialog
        show={isConfirmDialogVisible}
        text={
          <>
            <LoaderOverlay show={showChangePartnerPreloader || isTargetPartnerValidating} />
            <p className={styles.warningText}>Warning! This action is not reversible!</p>
            <p>
              You are going to replace Partner ID: {partner.partnerId}
              <br />
              With Partner ID: {apolloPartner?.partnerId}
            </p>
          </>
        }
        onCancel={() => {
          setIsConfirmDialogVisible(false)
        }}
        onSubmit={onChangePartnerIdHandler}
        cancelButtonText={'Cancel'}
        submitButtonText={'Submit'}
      />
      <ModalDialog
        size={'auto'}
        show={isRejectDialogVisible}
        onClose={closeRejectAndConfirmDialog}
        content={getModalContent}
        closeOnOutsideClick={true}
      />
    </div>
  )
}
